import React, { useState } from 'react';
import { removeFromWatchlist, formatPrice, formatChange } from '../../../../services/stockApi';

const StockList = ({
  stocks = [],
  selectedCode,
  onSelect,
  onDelete,
  realtimePrices = {},
  favoriteStocks = new Set(),
}) => {
  const [deleting, setDeleting] = useState(null);
  const [hoveredCode, setHoveredCode] = useState(null);

  const handleDelete = async (code, e) => {
    e.stopPropagation();

    if (!window.confirm(`${code} 종목을 삭제하시겠습니까?`)) {
      return;
    }

    setDeleting(code);
    try {
      const response = await removeFromWatchlist(code);
      setDeleting(null);

      if (response.success) {
        if (onDelete) {
          onDelete();
        }
      } else {
        alert(response.error || '삭제 실패');
      }
    } catch (err) {
      setDeleting(null);
      alert('삭제 중 오류 발생');
    }
  };

  if (stocks.length === 0) {
    return (
      <div className="py-8 px-4 text-center">
        <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-xl flex items-center justify-center">
          <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        </div>
        <p className="text-[13px] text-gray-400 mb-1">등록된 종목이 없습니다</p>
        <p className="text-[11px] text-gray-300">종목코드를 입력하여 추가하세요</p>
      </div>
    );
  }

  return (
    <div className="py-1 px-2 flex flex-col gap-0.5">
      {stocks.map((stock) => {
        const isSelected = selectedCode === stock.code || selectedCode === stock.symbol;
        const isHovered = hoveredCode === stock.code || hoveredCode === stock.symbol;
        const code = stock.code || stock.symbol;
        const isFavorite = favoriteStocks.has(code);

        // 실시간 가격 우선 사용
        const realtimePrice = realtimePrices[code];
        const currentPrice = realtimePrice?.current_price ?? realtimePrice?.price ?? stock.price;
        const changeRate = realtimePrice?.change_rate ?? realtimePrice?.change ?? stock.change;

        return (
          <div
            key={code}
            onClick={() => onSelect(code)}
            onMouseEnter={() => setHoveredCode(code)}
            onMouseLeave={() => setHoveredCode(null)}
            className={`relative py-2 px-2.5 rounded-lg cursor-pointer transition-all duration-150 hover:bg-gray-50 ${isSelected ? 'bg-blue-500 text-white shadow-sm' : ''}`}
          >
            {/* 삭제 버튼 */}
            <div className={`absolute top-1/2 right-1.5 -translate-y-1/2 z-10 transition-opacity duration-150 ${isSelected || isHovered ? 'opacity-100' : 'opacity-0'}`}>
              <button
                onClick={(e) => handleDelete(code, e)}
                disabled={deleting === code}
                className={`p-1 bg-transparent border-none rounded cursor-pointer flex items-center justify-center transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${isSelected ? 'text-white/70 hover:bg-white/20 hover:text-white' : 'text-gray-400 hover:bg-gray-100 hover:text-red-500'}`}
                title="삭제"
              >
                {deleting === code ? (
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>

            {/* 종목 정보 */}
            <div className="pr-5">
              {/* 1줄: 종목명 + 즐겨찾기 아이콘 */}
              <div className="flex items-center gap-1 mb-0.5">
                <span className={`font-medium text-[13px] truncate whitespace-nowrap overflow-hidden ${isSelected ? 'text-white' : 'text-gray-900'}`}>{stock.name}</span>
                {isFavorite && (
                  <span className={`flex-shrink-0 flex items-center ${isSelected ? 'text-amber-300' : 'text-amber-500'}`} title="즐겨찾기">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  </span>
                )}
              </div>

              {/* 2줄: 종목코드 (왼쪽) + 현재가/등락률 (오른쪽) */}
              <div className="flex items-center justify-between mt-0">
                <span className={`text-[11px] ${isSelected ? 'text-white/60' : 'text-gray-400'}`}>{code}</span>
                {currentPrice != null && (
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[11px] font-semibold transition-all duration-300 ${isSelected ? 'text-white' : 'text-gray-900'}`}>{formatPrice(currentPrice)}</span>
                    {changeRate != null && (
                      <span className={`text-[11px] font-bold py-0.5 px-1 rounded transition-all duration-300 ${
                        isSelected
                          ? changeRate > 0
                            ? 'bg-red-500/30 text-red-200'
                            : changeRate < 0
                            ? 'bg-blue-500/30 text-blue-200'
                            : 'bg-white/20 text-white/70'
                          : changeRate > 0
                          ? 'bg-red-50 text-red-500'
                          : changeRate < 0
                          ? 'bg-blue-50 text-blue-500'
                          : 'bg-gray-100 text-gray-400'
                      }`}>
                        {formatChange(changeRate)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StockList;
